import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@vns/core';
import { supabase } from './supabase';
import { fetchSubcontractorTransactions, cleanDisplayRemarks } from './transactions';
import type { ExportColumn } from './export';
import type { IconName } from '../components/ui';

/**
 * Reports.
 *
 * Each report is a declaration, not a page: which filters it offers, which
 * columns it shows, and how to fetch its rows. One generic runner renders any
 * of them, so adding a tenth report means adding one entry here.
 *
 * Every fetcher filters on approval_status = 'approved'. A report is what the
 * business will act on — send to an auditor, chase a client with — so a pending
 * site entry must never appear in one.
 */

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

const rel = (v: unknown, k = 'name') => (v as Record<string, string> | null)?.[k] ?? '—';
const day = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const num = (n: unknown) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const title = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

export interface ReportFilters {
  from?: string;
  to?: string;
  projectId?: string;
  clientId?: string;
  supplierId?: string;
  subcontractorId?: string;
  materialId?: string;
  workTypeId?: string;
  status?: string;
  paymentType?: string;
  direction?: string;
  mode?: string;
}

export type FilterKey = keyof ReportFilters;

export interface ReportDef {
  key: string;
  title: string;
  description: string;
  icon: IconName;
  /** Which filter controls the runner should show. Dates are always shown. */
  filters: FilterKey[];
  columns: ExportColumn[];
  fetch: (f: ReportFilters) => Promise<Array<Record<string, unknown>>>;
  /** Optional footer totals, keyed by column. */
  totals?: (rows: Array<Record<string, unknown>>) => Record<string, string>;
}

/** Applies the date window every report shares. */
function dateRange<T extends { gte: (c: string, v: string) => T; lte: (c: string, v: string) => T }>(
  q: T, col: string, f: ReportFilters,
): T {
  let out = q;
  if (f.from) out = out.gte(col, f.from);
  if (f.to) out = out.lte(col, f.to);
  return out;
}

/* ============================== definitions ============================== */

export const REPORTS: ReportDef[] = [
  /* ------------------------------- client ------------------------------- */
  {
    key: 'client',
    title: 'Client Report',
    description: 'Every client with their projects, what they have paid and what is still due.',
    icon: 'users',
    filters: ['clientId', 'status'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'client', label: 'Client' },
      { key: 'city', label: 'City' },
      { key: 'project', label: 'Project' },
      { key: 'location', label: 'Location' },
      { key: 'value', label: 'Project value', numeric: true, money: true },
      { key: 'received', label: 'Received', numeric: true, money: true },
      { key: 'pending', label: 'Pending', numeric: true, money: true },
      { key: 'status', label: 'Status' },
    ],
    fetch: async (f) => {
      let q = supabase
        .from('projects')
        .select('id, name, location, status, value_paise, start_date, created_at, client_id, clients(name, city)')
        .is('deleted_at', null);
      if (f.clientId) q = q.eq('client_id', f.clientId);
      if (f.status) q = q.eq('status', f.status);
      const projects = unwrap(await q);

      const payments = unwrap(
        await supabase.from('client_payments')
          .select('project_id, amount_paise')
          .eq('approval_status', 'approved').is('deleted_at', null),
      );
      const paid = new Map<string, number>();
      for (const p of payments) paid.set(p.project_id, (paid.get(p.project_id) ?? 0) + p.amount_paise);

      return projects
        .sort((a, b) => rel(a.clients).localeCompare(rel(b.clients)) || a.name.localeCompare(b.name))
        .map((p, i) => {
          const received = paid.get(p.id) ?? 0;
          return {
            sno: i + 1,
            date: day(p.start_date || p.created_at),
            client: rel(p.clients),
            city: rel(p.clients, 'city'),
            project: p.name,
            location: p.location ?? '—',
            value: formatMoney(p.value_paise),
            received: formatMoney(received),
            pending: formatMoney(Math.max(0, p.value_paise - received)),
            status: title(p.status),
            _value: p.value_paise, _received: received,
            _pending: Math.max(0, p.value_paise - received),
          };
        });
    },
    totals: (rows) => ({
      value: formatMoney(rows.reduce((s, r) => s + Number(r._value ?? 0), 0)),
      received: formatMoney(rows.reduce((s, r) => s + Number(r._received ?? 0), 0)),
      pending: formatMoney(rows.reduce((s, r) => s + Number(r._pending ?? 0), 0)),
    }),
  },

  /* ------------------------------ material ------------------------------ */
  {
    key: 'material',
    title: 'Material Report',
    description: 'Every material purchase with supplier, rate, amount paid and balance.',
    icon: 'box',
    filters: ['projectId', 'supplierId', 'materialId'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'material', label: 'Material' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'invoice', label: 'Invoice no.' },
      { key: 'quantity', label: 'Qty', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total', numeric: true, money: true },
      { key: 'paid', label: 'Paid', numeric: true, money: true },
      { key: 'balance', label: 'Balance', numeric: true, money: true },
    ],
    fetch: async (f) => {
      let q = supabase
        .from('material_purchases')
        .select('material_id, purchased_on, quantity, rate_paise, amount_paise, advance_paid_paise, invoice_no, projects(name), materials(id, name), suppliers(name), units(short_name)')
        .eq('approval_status', 'approved').is('deleted_at', null)
        .order('purchased_on', { ascending: false });
      if (f.projectId) q = q.eq('project_id', f.projectId);
      if (f.supplierId) q = q.eq('supplier_id', f.supplierId);
      if (f.materialId) q = q.eq('material_id', f.materialId);
      const rows = unwrap(await dateRange(q, 'purchased_on', f));

      return rows.map((r, i) => ({
        sno: i + 1,
        _materialId: r.material_id || (r.materials as unknown as { id?: string } | null)?.id || '',
        date: day(r.purchased_on),
        project: rel(r.projects),
        material: rel(r.materials),
        supplier: rel(r.suppliers),
        invoice: r.invoice_no ?? '—',
        quantity: num(r.quantity),
        unit: rel(r.units, 'short_name'),
        rate: formatMoney(r.rate_paise),
        amount: formatMoney(r.amount_paise),
        paid: formatMoney(r.advance_paid_paise),
        balance: formatMoney(r.amount_paise - r.advance_paid_paise),
        _amount: r.amount_paise, _paid: r.advance_paid_paise,
      }));
    },
    totals: (rows) => ({
      amount: formatMoney(rows.reduce((s, r) => s + Number(r._amount ?? 0), 0)),
      paid: formatMoney(rows.reduce((s, r) => s + Number(r._paid ?? 0), 0)),
      balance: formatMoney(rows.reduce((s, r) => s + Number(r._amount ?? 0) - Number(r._paid ?? 0), 0)),
    }),
  },

  /* -------------------------------- stock -------------------------------- */
  {
    key: 'stock',
    title: 'Stock Report',
    description: 'Project-wise inventory on hand, purchases (In), transfers (In/Out), consumption (Out), and stock valuation.',
    icon: 'box',
    filters: ['projectId', 'materialId'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project Site' },
      { key: 'material', label: 'Material' },
      { key: 'brand', label: 'Brand' },
      { key: 'unit', label: 'Unit' },
      { key: 'opening', label: 'Opening Stock', numeric: true },
      { key: 'purchased', label: 'Purchased (In)', numeric: true },
      { key: 'transfers', label: 'Net Transfers', numeric: true },
      { key: 'used', label: 'Used / Consumed (Out)', numeric: true },
      { key: 'available', label: 'Stock on Hand', numeric: true },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'value', label: 'Stock Valuation', numeric: true, money: true },
    ],
    fetch: async (f) => {
      let mq = supabase.from('materials')
        .select('id, name, brand, rate_paise, opening_qty, units(short_name)')
        .is('deleted_at', null).order('name');
      if (f.materialId) mq = mq.eq('id', f.materialId);
      const materials = unwrap(await mq);

      let prjQ = supabase.from('projects')
        .select('id, name')
        .is('deleted_at', null).order('name');
      if (f.projectId) prjQ = prjQ.eq('id', f.projectId);
      const projects = unwrap(await prjQ);

      let pq = supabase.from('material_purchases')
        .select('material_id, project_id, quantity, purchased_on')
        .eq('approval_status', 'approved').is('deleted_at', null);
      let uq = supabase.from('material_usage')
        .select('material_id, project_id, quantity, used_on')
        .eq('approval_status', 'approved').is('deleted_at', null);
      let tq = supabase.from('stock_transfers')
        .select('material_id, quantity, from_project_id, to_project_id, transferred_on')
        .eq('approval_status', 'approved').is('deleted_at', null);

      if (f.projectId) {
        pq = pq.eq('project_id', f.projectId);
        uq = uq.eq('project_id', f.projectId);
      }
      if (f.materialId) {
        pq = pq.eq('material_id', f.materialId);
        uq = uq.eq('material_id', f.materialId);
        tq = tq.eq('material_id', f.materialId);
      }

      const [purchases, usage, transfers] = await Promise.all([
        unwrap(await dateRange(pq, 'purchased_on', f)),
        unwrap(await dateRange(uq, 'used_on', f)),
        unwrap(await dateRange(tq, 'transferred_on', f)),
      ]);

      // Maps keyed by `${projectId}_${materialId}`
      const boughtMap = new Map<string, number>();
      const usedMap = new Map<string, number>();
      const transMap = new Map<string, number>();
      const latestActivityMap = new Map<string, string>();

      for (const p of purchases) {
        if (!p.project_id || !p.material_id) continue;
        const key = `${p.project_id}_${p.material_id}`;
        boughtMap.set(key, (boughtMap.get(key) ?? 0) + Number(p.quantity));
        if (p.purchased_on) {
          const cur = latestActivityMap.get(key) ?? '';
          if (p.purchased_on > cur) latestActivityMap.set(key, p.purchased_on);
        }
      }

      for (const u of usage) {
        if (!u.project_id || !u.material_id) continue;
        const key = `${u.project_id}_${u.material_id}`;
        usedMap.set(key, (usedMap.get(key) ?? 0) + Number(u.quantity));
        if (u.used_on) {
          const cur = latestActivityMap.get(key) ?? '';
          if (u.used_on > cur) latestActivityMap.set(key, u.used_on);
        }
      }

      for (const t of transfers) {
        const mid = t.material_id;
        const q = Number(t.quantity);
        if (t.to_project_id) {
          const toKey = `${t.to_project_id}_${mid}`;
          transMap.set(toKey, (transMap.get(toKey) ?? 0) + q);
          if (t.transferred_on) {
            const cur = latestActivityMap.get(toKey) ?? '';
            if (t.transferred_on > cur) latestActivityMap.set(toKey, t.transferred_on);
          }
        }
        if (t.from_project_id) {
          const fromKey = `${t.from_project_id}_${mid}`;
          transMap.set(fromKey, (transMap.get(fromKey) ?? 0) - q);
          if (t.transferred_on) {
            const cur = latestActivityMap.get(fromKey) ?? '';
            if (t.transferred_on > cur) latestActivityMap.set(fromKey, t.transferred_on);
          }
        }
      }

      const rows: Array<Record<string, unknown>> = [];

      for (const proj of projects) {
        for (const m of materials) {
          const key = `${proj.id}_${m.id}`;
          const b = boughtMap.get(key) ?? 0;
          const u = usedMap.get(key) ?? 0;
          const t = transMap.get(key) ?? 0;
          const open = 0; // Site opening stock
          const available = open + b + t - u;
          const ratePaise = Number(m.rate_paise ?? 0);
          const valPaise = Math.round(Math.max(0, available) * ratePaise);
          const hasActivity = b > 0 || u > 0 || Math.abs(t) > 0;
          const asOfDate = f.to || latestActivityMap.get(key) || f.from || new Date().toISOString().slice(0, 10);

          if (hasActivity || (f.materialId && f.projectId)) {
            rows.push({
              date: day(asOfDate),
              project: proj.name,
              _projectId: proj.id,
              _materialId: m.id,
              material: m.name,
              brand: m.brand || '—',
              unit: rel(m.units, 'short_name'),
              opening: num(open),
              _opening: open,
              purchased: num(b),
              _purchased: b,
              transfers: t > 0 ? `+${num(t)}` : (t < 0 ? num(t) : '—'),
              _transfers: t,
              used: num(u),
              _used: u,
              available: num(available),
              _available: available,
              rate: formatMoney(ratePaise),
              _rate: ratePaise,
              value: formatMoney(valPaise),
              _value: valPaise,
              _hasActivity: hasActivity,
            });
          }
        }
      }

      return rows
        .sort((a, b) =>
          String(a.project).localeCompare(String(b.project)) ||
          Number(b._value ?? 0) - Number(a._value ?? 0) ||
          String(a.material).localeCompare(String(b.material))
        )
        .map((r, i) => ({ sno: i + 1, ...r }));
    },
    totals: (rows) => ({
      value: formatMoney(rows.reduce((s, r) => s + Number(r._value ?? 0), 0)),
    }),
  },

  /* ------------------------------- supplier ------------------------------- */
  {
    key: 'supplier',
    title: 'Supplier Report',
    description: 'Purchase value, paid and outstanding for each supplier.',
    icon: 'truck',
    filters: ['projectId', 'supplierId'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'city', label: 'City' },
      { key: 'gst', label: 'GST number' },
      { key: 'orders', label: 'Purchases', numeric: true },
      { key: 'value', label: 'Credit', numeric: true, money: true },
      { key: 'paid', label: 'Debit', numeric: true, money: true },
      { key: 'balance', label: 'Outstanding Balance', numeric: true, money: true },
    ],
    fetch: async (f) => {
      let sq = supabase.from('suppliers')
        .select('id, name, city, gst_number').is('deleted_at', null).order('name');
      if (f.supplierId) sq = sq.eq('id', f.supplierId);
      const suppliers = unwrap(await sq);

      let pq = supabase.from('material_purchases')
        .select('supplier_id, amount_paise, advance_paid_paise, purchased_on, project_id')
        .eq('approval_status', 'approved').is('deleted_at', null);
      if (f.projectId) pq = pq.eq('project_id', f.projectId);
      if (f.supplierId) pq = pq.eq('supplier_id', f.supplierId);

      let payQ = supabase.from('supplier_payments')
        .select('supplier_id, amount_paise, paid_on, project_id')
        .eq('approval_status', 'approved').is('deleted_at', null);
      if (f.projectId) payQ = payQ.eq('project_id', f.projectId);
      if (f.supplierId) payQ = payQ.eq('supplier_id', f.supplierId);

      const [purchases, payments] = await Promise.all([
        unwrap(await dateRange(pq, 'purchased_on', f)),
        unwrap(await dateRange(payQ, 'paid_on', f)),
      ]);

      const agg = new Map<string, { n: number; value: number; paid: number; latestDate: string }>();
      for (const p of purchases) {
        const a = agg.get(p.supplier_id) ?? { n: 0, value: 0, paid: 0, latestDate: '' };
        a.n += 1;
        a.value += p.amount_paise;
        a.paid += (p.advance_paid_paise ?? 0);
        if (p.purchased_on && p.purchased_on > a.latestDate) a.latestDate = p.purchased_on;
        agg.set(p.supplier_id, a);
      }
      // Later settlements count towards paid alongside the advance at purchase.
      for (const p of payments) {
        const a = agg.get(p.supplier_id) ?? { n: 0, value: 0, paid: 0, latestDate: '' };
        a.paid += p.amount_paise;
        if (p.paid_on && p.paid_on > a.latestDate) a.latestDate = p.paid_on;
        agg.set(p.supplier_id, a);
      }

      return suppliers
        .map((s) => {
          const a = agg.get(s.id) ?? { n: 0, value: 0, paid: 0, latestDate: '' };
          const asOfDate = f.to || a.latestDate || new Date().toISOString().slice(0, 10);
          return {
            date: day(asOfDate),
            supplier: s.name,
            city: s.city ?? '—',
            gst: s.gst_number ?? '—',
            orders: a.n,
            value: formatMoney(a.value),
            paid: formatMoney(a.paid),
            balance: formatMoney(Math.max(0, a.value - a.paid)),
            _value: a.value,
            _paid: a.paid,
            _balance: Math.max(0, a.value - a.paid),
            _orders: a.n,
          };
        })
        .filter((r) => r._orders > 0 || r._value > 0 || r._paid > 0 || !!f.supplierId)
        .sort((a, b) => b._balance - a._balance || b._value - a._value || String(a.supplier).localeCompare(String(b.supplier)))
        .map((r, i) => ({ sno: i + 1, ...r }));
    },
    totals: (rows) => ({
      orders: String(rows.reduce((s, r) => s + Number(r._orders ?? 0), 0)),
      value: formatMoney(rows.reduce((s, r) => s + Number(r._value ?? 0), 0)),
      paid: formatMoney(rows.reduce((s, r) => s + Number(r._paid ?? 0), 0)),
      balance: formatMoney(rows.reduce((s, r) => s + Math.max(0, Number(r._value ?? 0) - Number(r._paid ?? 0)), 0)),
    }),
  },

  /* ---------------------------- sub-contractor ---------------------------- */
  {
    key: 'subcontractor',
    title: 'Sub Contractor Report',
    description: 'Sub-contractor work, trade headcounts, labour credits, payments (debit), and running balance due.',
    icon: 'hardhat',
    filters: ['projectId', 'subcontractorId'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'subcontractor', label: 'Sub-contractor' },
      { key: 'trades', label: 'Work Trades' },
      { key: 'workers', label: 'Labours (Workers)', numeric: true },
      { key: 'credit', label: 'Credit (₹)', numeric: true, money: true },
      { key: 'debit', label: 'Debit (₹)', numeric: true, money: true },
      { key: 'balance', label: 'Balance Due (₹)', numeric: true, money: true },
      { key: 'remarks', label: 'Remarks' },
    ],
    fetch: async (f) => {
      const rows = await fetchSubcontractorTransactions({
        projectId: f.projectId,
        subcontractorId: f.subcontractorId,
        from: f.from,
        to: f.to,
      });

      return rows.map((r, i) => ({
        sno: i + 1,
        date: day(r.date),
        project: r.projectName,
        subcontractor: r.subcontractorName,
        trades: r.tradesSummary,
        workers: r.totalWorkers > 0 ? r.totalWorkers : '—',
        credit: r.overallTotalPaise > 0 ? formatMoney(r.overallTotalPaise) : '—',
        debit: r.paidAmountPaise > 0 ? formatMoney(r.paidAmountPaise) : '—',
        balance: formatMoney(r.balanceAmountPaise),
        remarks: r.remarks || '—',
        _workers: r.totalWorkers,
        _credit: r.overallTotalPaise,
        _debit: r.paidAmountPaise,
        _balance: r.balanceAmountPaise,
      }));
    },
    totals: (rows) => ({
      workers: `${num(rows.reduce((s, r) => s + Number(r._workers ?? 0), 0))} labours`,
      credit: formatMoney(rows.reduce((s, r) => s + Number(r._credit ?? 0), 0)),
      debit: formatMoney(rows.reduce((s, r) => s + Number(r._debit ?? 0), 0)),
      balance: formatMoney(rows.reduce((s, r) => s + Number(r._balance ?? 0), 0)),
    }),
  },

  /* ------------------------------- payment ------------------------------- */
  {
    key: 'payment',
    title: 'Payment Report',
    description: 'Every payment in and out, across client receipts, supplier bills, purchase advances, subcontractor labour, site expenses, and bank fund transfers.',
    icon: 'transfer',
    filters: ['projectId', 'paymentType', 'direction', 'mode'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'direction', label: 'In / Out' },
      { key: 'party', label: 'Party' },
      { key: 'partyType', label: 'Payment Type' },
      { key: 'project', label: 'Project' },
      { key: 'amount', label: 'Amount', numeric: true, money: true },
      { key: 'mode', label: 'Mode' },
      { key: 'reference', label: 'Reference / Details' },
    ],
    fetch: async (f) => {
      const [clientPays, supplierPays, purchaseAdvances, subPays, siteExpenses, fundTransfers] = await Promise.all([
        dateRange(supabase.from('client_payments')
          .select('paid_on, amount_paise, payment_type, reference_no, project_id, projects(name, clients(name))')
          .eq('approval_status', 'approved').is('deleted_at', null), 'paid_on', f),
        dateRange(supabase.from('supplier_payments')
          .select('paid_on, amount_paise, mode, reference_no, project_id, suppliers(name), projects(name)')
          .eq('approval_status', 'approved').is('deleted_at', null), 'paid_on', f),
        dateRange(supabase.from('material_purchases')
          .select('purchased_on, advance_paid_paise, invoice_no, project_id, suppliers(name), projects(name)')
          .gt('advance_paid_paise', 0)
          .eq('approval_status', 'approved').is('deleted_at', null), 'purchased_on', f),
        dateRange(supabase.from('subcontractor_payments')
          .select('paid_on, amount_paise, mode, reference_no, project_id, subcontractors(name), projects(name)')
          .eq('approval_status', 'approved').is('deleted_at', null), 'paid_on', f),
        dateRange(supabase.from('expenses')
          .select('spent_on, amount_paise, title, paid_to, bill_no, project_id, projects(name), expense_categories(name)')
          .eq('approval_status', 'approved').is('deleted_at', null), 'spent_on', f),
        dateRange(supabase.from('fund_transfers')
          .select('transferred_on, amount_paise, mode, reference_no, remarks, from_account:bank_accounts!fund_transfers_from_account_id_fkey(bank_name), to_account:bank_accounts!fund_transfers_to_account_id_fkey(bank_name)')
          .is('deleted_at', null), 'transferred_on', f),
      ]);

      const rows: Array<Record<string, unknown>> = [];
      const keepProj = (pid: unknown) => !f.projectId || pid === f.projectId;

      // 1. Client receipts (Money In)
      for (const r of unwrap(await clientPays)) {
        if (!keepProj(r.project_id)) continue;
        const proj = r.projects as unknown as { name: string; clients: { name: string } | null } | null;
        const pTypeLabel = r.payment_type ? `Client ${title(r.payment_type)}` : 'Client Receipt';
        rows.push({
          date: day(r.paid_on), _sort: r.paid_on,
          direction: 'Received', _direction: 'Received',
          party: proj?.clients?.name ?? '—',
          partyType: pTypeLabel, _typeKey: 'client',
          project: proj?.name ?? '—',
          amount: formatMoney(r.amount_paise),
          mode: 'DIRECT / BANK', _mode: 'DIRECT',
          reference: r.reference_no ?? '—',
          _in: r.amount_paise, _out: 0,
        });
      }

      // 2. Supplier Payments (Money Out)
      for (const r of unwrap(await supplierPays)) {
        if (!keepProj(r.project_id)) continue;
        const modeStr = String(r.mode ?? 'BANK').toUpperCase();
        rows.push({
          date: day(r.paid_on), _sort: r.paid_on,
          direction: 'Paid', _direction: 'Paid',
          party: rel(r.suppliers),
          partyType: 'Supplier Settlement', _typeKey: 'supplier',
          project: rel(r.projects),
          amount: formatMoney(r.amount_paise),
          mode: modeStr, _mode: modeStr,
          reference: r.reference_no ?? '—',
          _in: 0, _out: r.amount_paise,
        });
      }

      // 3. Material Purchase Direct Advances (Money Out)
      for (const r of unwrap(await purchaseAdvances)) {
        if (!keepProj(r.project_id)) continue;
        rows.push({
          date: day(r.purchased_on), _sort: r.purchased_on,
          direction: 'Paid', _direction: 'Paid',
          party: rel(r.suppliers),
          partyType: 'Purchase Advance', _typeKey: 'purchase_advance',
          project: rel(r.projects),
          amount: formatMoney(r.advance_paid_paise),
          mode: 'ADVANCE', _mode: 'ADVANCE',
          reference: r.invoice_no ? `Invoice ${r.invoice_no}` : 'Purchase advance',
          _in: 0, _out: r.advance_paid_paise,
        });
      }

      // 4. Subcontractor Payments (Money Out)
      for (const r of unwrap(await subPays)) {
        if (!keepProj(r.project_id)) continue;
        const modeStr = String(r.mode ?? 'CASH').toUpperCase();
        rows.push({
          date: day(r.paid_on), _sort: r.paid_on,
          direction: 'Paid', _direction: 'Paid',
          party: rel(r.subcontractors),
          partyType: 'Subcontractor Payment', _typeKey: 'subcontractor',
          project: rel(r.projects),
          amount: formatMoney(r.amount_paise),
          mode: modeStr, _mode: modeStr,
          reference: r.reference_no ?? '—',
          _in: 0, _out: r.amount_paise,
        });
      }

      // 5. Site Expenses (Money Out)
      for (const r of unwrap(await siteExpenses)) {
        if (!keepProj(r.project_id)) continue;
        const catName = rel(r.expense_categories);
        const partyLabel = r.paid_to || catName || r.title || 'Vendor';
        rows.push({
          date: day(r.spent_on), _sort: r.spent_on,
          direction: 'Paid', _direction: 'Paid',
          party: partyLabel,
          partyType: catName ? `Expense (${catName})` : 'Site Expense', _typeKey: 'expense',
          project: rel(r.projects),
          amount: formatMoney(r.amount_paise),
          mode: 'CASH', _mode: 'CASH',
          reference: r.bill_no ? `Bill: ${r.bill_no}` : (r.title ?? '—'),
          _in: 0, _out: r.amount_paise,
        });
      }

      // 6. Fund Transfers (Internal Account Movements - shown when no project filter or for all)
      if (!f.projectId) {
        for (const r of unwrap(await fundTransfers)) {
          const fromAcc = rel(r.from_account, 'bank_name');
          const toAcc = rel(r.to_account, 'bank_name');
          const modeStr = String(r.mode ?? 'INTERNAL').toUpperCase();
          rows.push({
            date: day(r.transferred_on), _sort: r.transferred_on,
            direction: 'Transfer', _direction: 'Transfer',
            party: `${fromAcc} → ${toAcc}`,
            partyType: 'Fund Transfer', _typeKey: 'transfer',
            project: 'Company Accounts',
            amount: formatMoney(r.amount_paise),
            mode: modeStr, _mode: modeStr,
            reference: r.reference_no ? `Ref: ${r.reference_no}` : (r.remarks ?? '—'),
            _in: 0, _out: 0,
          });
        }
      }

      // Apply dynamic filters: paymentType, direction, mode
      return rows
        .filter((r) => {
          if (f.paymentType && r._typeKey !== f.paymentType) return false;
          if (f.direction && r._direction !== f.direction) return false;
          if (f.mode && !String(r._mode).includes(f.mode.toUpperCase())) return false;
          return true;
        })
        .sort((a, b) => String(b._sort).localeCompare(String(a._sort)))
        .map((r, i) => ({ sno: i + 1, ...r }));
    },
    totals: (rows) => {
      const inn = rows.reduce((s, r) => s + Number(r._in ?? 0), 0);
      const out = rows.reduce((s, r) => s + Number(r._out ?? 0), 0);
      return {
        amount: `In: ${formatMoney(inn)} | Out: ${formatMoney(out)}`,
        partyType: `Net: ${formatMoney(inn - out)}`,
      };
    },
  },

  /* -------------------------- stock transfer -------------------------- */
  {
    key: 'transfer',
    title: 'Site Transfer Report',
    description: 'Material moved between sites, with valuation, vehicle and driver.',
    icon: 'repeat',
    filters: ['projectId', 'materialId'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'material', label: 'Material' },
      { key: 'from', label: 'From site' },
      { key: 'to', label: 'To site' },
      { key: 'quantity', label: 'Qty', numeric: true },
      { key: 'unit', label: 'Unit' },
      { key: 'rate', label: 'Rate', numeric: true, money: true },
      { key: 'amount', label: 'Total Value', numeric: true, money: true },
      { key: 'vehicle', label: 'Vehicle' },
      { key: 'driver', label: 'Driver' },
      { key: 'remarks', label: 'Remarks' },
    ],
    fetch: async (f) => {
      let q = supabase.from('stock_transfers')
        .select('material_id, transferred_on, quantity, rate_paise, amount_paise, vehicle_no, driver_name, remarks, from_project_id, to_project_id, materials(id, name), units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(name), to_project:projects!stock_transfers_to_project_id_fkey(name)')
        .eq('approval_status', 'approved').is('deleted_at', null)
        .order('transferred_on', { ascending: false });
      if (f.materialId) q = q.eq('material_id', f.materialId);
      const rows = unwrap(await dateRange(q, 'transferred_on', f));

      return rows
        // A project filter should catch transfers in either direction.
        .filter((r) => !f.projectId || r.from_project_id === f.projectId || r.to_project_id === f.projectId)
        .map((r, i) => ({
          sno: i + 1,
          _materialId: r.material_id || (r.materials as unknown as { id?: string } | null)?.id || '',
          date: day(r.transferred_on),
          material: rel(r.materials),
          from: rel(r.from_project),
          to: rel(r.to_project),
          quantity: num(r.quantity),
          unit: rel(r.units, 'short_name'),
          rate: r.rate_paise != null ? formatMoney(r.rate_paise) : '—',
          amount: r.amount_paise != null ? formatMoney(r.amount_paise) : '—',
          vehicle: r.vehicle_no ?? '—',
          driver: r.driver_name ?? '—',
          remarks: r.remarks ?? '—',
          _amount: r.amount_paise ?? 0,
        }));
    },
    totals: (rows) => ({
      amount: formatMoney(rows.reduce((s, r) => s + Number(r._amount ?? 0), 0)),
    }),
  },

  /* ------------------------------- finance ------------------------------- */
  {
    key: 'finance',
    title: 'Finance Report',
    description: 'Money in and out per project, and what each project has cost so far.',
    icon: 'wallet',
    filters: ['projectId', 'clientId', 'status'],
    columns: [
      { key: 'sno', label: 'S.No', numeric: true },
      { key: 'date', label: 'Date' },
      { key: 'project', label: 'Project' },
      { key: 'client', label: 'Client' },
      { key: 'status', label: 'Status' },
      { key: 'value', label: 'Project value', numeric: true, money: true },
      { key: 'received', label: 'Received', numeric: true, money: true },
      { key: 'material', label: 'Material cost', numeric: true, money: true },
      { key: 'labour', label: 'Labour cost', numeric: true, money: true },
      { key: 'expenses', label: 'Expenses', numeric: true, money: true },
      { key: 'spent', label: 'Total spent', numeric: true, money: true },
      { key: 'position', label: 'Received − spent', numeric: true, money: true },
    ],
    fetch: async (f) => {
      let pq = supabase.from('projects')
        .select('id, name, start_date, status, value_paise, client_id, clients(name)')
        .is('deleted_at', null).order('name');
      if (f.projectId) pq = pq.eq('id', f.projectId);
      if (f.clientId) pq = pq.eq('client_id', f.clientId);
      if (f.status) pq = pq.eq('status', f.status);
      const projects = unwrap(await pq);

      const [pays, mats, labour, exps] = await Promise.all([
        unwrap(await dateRange(supabase.from('client_payments').select('project_id, amount_paise, paid_on')
          .eq('approval_status', 'approved').is('deleted_at', null), 'paid_on', f)),
        unwrap(await dateRange(supabase.from('material_purchases').select('project_id, amount_paise, purchased_on')
          .eq('approval_status', 'approved').is('deleted_at', null), 'purchased_on', f)),
        unwrap(await dateRange(supabase.from('subcontractor_labour').select('project_id, total_amount_paise, worked_on')
          .eq('approval_status', 'approved').is('deleted_at', null), 'worked_on', f)),
        unwrap(await dateRange(supabase.from('expenses').select('project_id, amount_paise, spent_on')
          .eq('approval_status', 'approved').is('deleted_at', null), 'spent_on', f)),
      ]);

      const sum = (rows: Array<Record<string, unknown>>, col: string) => {
        const m = new Map<string, number>();
        for (const r of rows) {
          const k = String(r.project_id);
          m.set(k, (m.get(k) ?? 0) + Number(r[col] ?? 0));
        }
        return m;
      };
      const received = sum(pays, 'amount_paise');
      const material = sum(mats, 'amount_paise');
      const labourCost = sum(labour, 'total_amount_paise');
      const expense = sum(exps, 'amount_paise');

      return projects.map((p, i) => {
        const r = received.get(p.id) ?? 0;
        const m = material.get(p.id) ?? 0;
        const l = labourCost.get(p.id) ?? 0;
        const e = expense.get(p.id) ?? 0;
        const spent = m + l + e;
        return {
          sno: i + 1,
          date: day(p.start_date),
          project: p.name,
          client: rel(p.clients),
          status: title(p.status),
          value: formatMoney(p.value_paise),
          received: formatMoney(r),
          material: formatMoney(m),
          labour: formatMoney(l),
          expenses: formatMoney(e),
          spent: formatMoney(spent),
          position: formatMoney(r - spent),
          _value: p.value_paise, _received: r, _material: m,
          _labour: l, _expenses: e, _spent: spent,
        };
      });
    },
    totals: (rows) => ({
      value: formatMoney(rows.reduce((s, r) => s + Number(r._value ?? 0), 0)),
      received: formatMoney(rows.reduce((s, r) => s + Number(r._received ?? 0), 0)),
      material: formatMoney(rows.reduce((s, r) => s + Number(r._material ?? 0), 0)),
      labour: formatMoney(rows.reduce((s, r) => s + Number(r._labour ?? 0), 0)),
      expenses: formatMoney(rows.reduce((s, r) => s + Number(r._expenses ?? 0), 0)),
      spent: formatMoney(rows.reduce((s, r) => s + Number(r._spent ?? 0), 0)),
      position: formatMoney(rows.reduce((s, r) => s + Number(r._received ?? 0) - Number(r._spent ?? 0), 0)),
    }),
  },
];

export interface ReportCard {
  key: string;
  title: string;
  description: string;
  icon: IconName;
}

/**
 * Cards for the Reports screen, in display order.
 *
 * Every flat-table report in REPORTS gets a card automatically. Project
 * Report does not appear in REPORTS at all — it is one project drilled into
 * across nine sections, not a table of rows, so it has its own dedicated
 * page (routes/ProjectReport.tsx) rather than a slot in the generic runner.
 * Its card is declared here by hand, in the same spot the old flat "one row
 * per project" report used to sit.
 */
const cardFor = (key: string): ReportCard => {
  const r = REPORTS.find((x) => x.key === key)!;
  return { key: r.key, title: r.title, description: r.description, icon: r.icon };
};

export const REPORT_CARDS: ReportCard[] = [
  cardFor('client'),
  {
    key: 'project',
    title: 'Project Report',
    description: 'Pick one project — every payment, material, worker, supplier, expense, transfer and rupee logged against it, in one place.',
    icon: 'briefcase',
  },
  {
    key: 'daily',
    title: 'Daily Project Report (Day Book)',
    description: 'Chronological client day book of all site costs (materials, labour, expenses) and client collections with date-range balance calculation.',
    icon: 'calendar',
  },
  {
    key: 'material',
    title: 'Material Report',
    description: 'Pick one material — purchases, bills, site consumption, stock transfers, project distribution, and valuation in one place.',
    icon: 'box',
  },
  cardFor('stock'),
  cardFor('supplier'),
  cardFor('subcontractor'),
  cardFor('payment'),
  cardFor('transfer'),
  cardFor('finance'),
];

export const getReport = (key: string) => REPORTS.find((r) => r.key === key);

export function useReport(key: string, filters: ReportFilters, enabled: boolean) {
  const def = getReport(key);
  return useQuery({
    queryKey: ['report', key, filters],
    enabled: enabled && !!def,
    queryFn: () => def!.fetch(filters),
  });
}
